import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const servicesData = [
  {
    id: 'web',
    title: 'Website Design & Dev',
    icon: 'FiLayout',
    desc: 'Custom websites built to represent your business, communicate your value, and convert visitors into customers.',
    points: ['Custom design', 'WordPress dev', 'E-commerce', 'Landing pages', 'Analytics tracking']
  },
  {
    id: 'seo',
    title: 'Search Engine Optimization',
    icon: 'FiSearch',
    desc: 'Get your business discovered by the people already searching for your products or services.',
    points: ['SEO audits', 'Keyword research', 'Local SEO', 'Technical SEO', 'AI search visibility']
  },
  {
    id: 'ads',
    title: 'Google Ads & Paid Media',
    icon: 'FiMousePointer',
    desc: 'Reach qualified customers when they are actively looking for what your business offers.',
    points: ['Search campaigns', 'Performance Max', 'Social ads', 'Conversion tracking', 'Lead-quality analysis']
  },
  {
    id: 'social',
    title: 'Social Media Management',
    icon: 'FiShare2',
    desc: 'Build a consistent, credible presence across the platforms your customers use.',
    points: ['Content planning', 'Graphic creation', 'Community management', 'Paid social ads', 'Performance reporting']
  },
  {
    id: 'email',
    title: 'Email Marketing',
    icon: 'FiMail',
    desc: 'Stay connected with prospects through campaigns built around action—not inbox clutter.',
    points: ['Campaign strategy', 'Lead-nurture sequences', 'Automated follow-up', 'List segmentation', 'CRM integration']
  },
  {
    id: 'software',
    title: 'Custom Software & CRM',
    icon: 'FiDatabase',
    desc: 'When off-the-shelf software does not fit your business, we create a solution that does.',
    points: ['Custom business software', 'Customer portals', 'CRM setup', 'Workflow automation', 'API integrations']
  },
  {
    id: 'ai',
    title: 'AI Callbots & Automation',
    icon: 'FiCpu',
    desc: 'Help your business respond faster, capture more leads, and reduce repetitive administrative work.',
    points: ['Answering calls', 'Qualifying leads', 'Scheduling appointments', 'CRM workflows', 'After-hours support']
  }
];

const Services = () => {
  return (
    <section id="services" className="light-section section-padding relative bg-white">
      <div className="container-custom">
        <div className="text-center max-w-3xl mx-auto mb-20 relative z-10">
          <span className="text-purple-600 font-medium tracking-widest text-base uppercase mb-4 block font-display">
            What We Do
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-gray-900 mb-6">
            One Team. Every Part of Your <br className="hidden md:block"/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">Digital Growth.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 relative z-10">
          {servicesData.map((service, idx) => (
            <motion.div 
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: idx * 0.1 }}
              className="bg-gray-50 rounded-3xl p-8 flex flex-col h-full border border-gray-100 hover:shadow-xl hover:border-cyan-200 hover:-translate-y-1 transition-all duration-300 group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl group-hover:bg-cyan-500/10 transition-all duration-500"></div>
              
              <div className="w-14 h-14 rounded-xl bg-white shadow-sm border border-gray-100 flex items-center justify-center text-cyan-600 mb-8 group-hover:scale-110 group-hover:border-cyan-300 transition-all duration-300">
                <SafeIcon icon={service.icon} className="text-2xl" />
              </div>
              
              <h3 className="text-2xl font-display font-bold text-gray-900 mb-4">{service.title}</h3>
              <p className="text-gray-600 mb-8 flex-grow font-light leading-relaxed text-base">{service.desc}</p>
              
              <ul className="space-y-3 mb-8">
                {service.points.map((point, i) => (
                  <li key={i} className="flex items-start text-base text-gray-600">
                    <SafeIcon icon="FiCheck" className="text-cyan-500 mt-1 mr-3 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}

          {/* CTA Card */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            className="rounded-3xl p-8 flex flex-col items-center justify-center text-center relative overflow-hidden col-span-1 md:col-span-2 xl:col-span-2 bg-gradient-to-br from-cyan-50 to-purple-50 border border-cyan-100"
          >
            <h3 className="text-3xl font-display font-bold text-gray-900 mb-4">Need a Custom Strategy?</h3>
            <p className="text-gray-600 mb-8 max-w-md text-base">Let's discuss how these services can be combined to accelerate your specific business goals.</p>
            <Link to="/" state={{ scrollTo: 'contact' }} className="btn-neon">Let's Talk</Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Services;